// src/utils/adminPasswordReset.ts
import {supabase} from '@/services/supabase';

/**
 * Require a host to reset their password on next login
 */
export const requireHostPasswordReset = async (
    hostEmail: string,
    adminUserId?: string
): Promise<boolean> => {
    try {
        const {data, error} = await supabase.rpc('require_host_password_reset', {
            p_host_email: hostEmail,
            p_requested_by: adminUserId || null
        });

        if (error) {
            console.error('Error requiring password reset:', error);
            throw new Error(error.message);
        }

        if (!data) {
            throw new Error('Host not found or not eligible for password reset');
        }

        return true;
    } catch (error) {
        console.error('Failed to require password reset:', error);
        throw error;
    }
};

/**
 * Bulk require password reset for multiple hosts
 */
export const bulkRequirePasswordReset = async (
    hostEmails: string[],
    adminUserId?: string
): Promise<{ success: string[]; failed: string[] }> => {
    const results = {success: [] as string[], failed: [] as string[]};

    for (const email of hostEmails) {
        try {
            await requireHostPasswordReset(email, adminUserId);
            results.success.push(email);
        } catch (error) {
            console.error(`Failed to require password reset for ${email}:`, error);
            results.failed.push(email);
        }
    }

    return results;
};

/**
 * Check if a host has password reset requirement
 */
export const checkHostPasswordResetStatus = async (hostEmail: string): Promise<{
    required: boolean;
    requestedAt: string | null;
    requestedBy: string | null;
}> => {
    try {
        const {data, error} = await supabase
            .from('game_hosts')
            .select('password_reset_required, password_reset_requested_at, password_reset_requested_by')
            .eq('email', hostEmail)
            .eq('invitation_status', 'accepted')
            .is('deleted_at', null)
            .single();

        if (error) {
            throw new Error(error.message);
        }

        return {
            required: data.password_reset_required || false,
            requestedAt: data.password_reset_requested_at,
            requestedBy: data.password_reset_requested_by
        };
    } catch (error) {
        console.error('Failed to check password reset status:', error);
        throw error;
    }
};
